import * as React from "react";
import classnames from "classnames";
import { useTheme } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../models/states/StoreState";
import {
    setCarrouselActiveVariableIndex,
    validationSetResult,
    setDefaultFocusStatus,
} from "../../redux/actions";
import useStyles, { resolveStatementBoxShadow } from "./CarrouselControl.styles";
import { useLanguage } from "../../hooks/useLanguage";
import { ControlProps } from "./BaseControl";
import { translate } from "../../models/utilities/translationUtilities";
import {
    useValidation,
    useDiscreteVariadicValidator,
    isAnswerRequired,
} from "../../hooks/useValidation";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ChoiceListControl } from "./ChoiceListControl";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { QuestionType } from "../../models/enums/QuestionType";
import {
    getChoiceListDisplayType,
    isQuestionSoftPrompt,
} from "../../models/utilities/questionUtilities";
import lottieGif from "../../assets/images/lottie.gif";
import { MOBILE_WIDTH, SMALL_WIDTH } from "../../consts";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import { CarrouselNavigation } from "./KeyboardNavigation/CarrouselNavigation";
import useRecall from "../../hooks/useRecall";
import { stripHtmlForAccessibleName } from "../../utils/WCAGHelper";
import type { Translatable } from "../../models/Translatable";
import type { QuestionModel } from "../../models/QuestionModel";
import {
    isCarrouselRadioLikeMode,
    computeCarrouselStatementTransform,
} from "../../models/utilities/carrouselUtilities";
import { PoliteStatusPortal } from "../shared/PoliteStatusPortal";
import { resolveCarrouselCornerBorderWidth } from "../../theme/carrouselThemeTokens";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import useTextDirection from "../../hooks/useTextDirection";
import type { Theme } from "../../types/Theme";

/** Plain-text accessible name for the carousel region (AC1: aria-label describing the question). */
function getCarouselRegionAriaLabel(question: QuestionModel, language: string): string {
    const title = stripHtmlForAccessibleName(translate(question, "TITLE", language) || "");
    const text = stripHtmlForAccessibleName(translate(question, "TEXT", language) || "");
    const shortText = stripHtmlForAccessibleName(translate(question, "SHORT_TEXT", language) || "");
    const hasTitle = title.length > 0;
    // DEV-98356: include short text alongside text rather than only as a fallback when text is
    // empty — previously short text was silently dropped whenever text was also present.
    const body = [text, shortText].filter((part) => part.length > 0).join(". ");
    const hasBody = body.length > 0;
    if (hasTitle && hasBody) {
        return `${title}. ${body}`.trim();
    }
    if (hasTitle) {
        return title;
    }
    if (hasBody) {
        return body;
    }
    return stripHtmlForAccessibleName(question?.name || "") || "Question";
}

/** Resolves a themed carousel ARIA label by looking up the theme key and substituting {current}/{total}. */
function resolveCarouselAriaLabel(
    theme: Translatable | null | undefined,
    key: string,
    language: string,
    current: number,
    total: number,
    fallbackTemplate: string,
): string {
    const raw = theme ? translate(theme, key, language) : "";
    const template = raw && String(raw).trim().length > 0 ? raw : fallbackTemplate;
    return template
        .replace(/\{current\}/g, String(current))
        .replace(/\{total\}/g, String(total));
}

interface StatementProps {
    id: string;
    key: string;
    text: string;
    color: string;
    imageUrl: string;
    isMobileView: boolean;
    isActiveItem: boolean;
    statementWidth: number;
    handleSwipe: (direction: string) => void;
    isShowLottieGesture: boolean;
    /** From theme CarouselAriaCurrentStatement (interpolated for this slide index). */
    carouselStatementPositionLabel: string;
}

const MOBILE_DIMENSIONS = 270;
const RESIZE_CAROUSEL = "resize-carousel";
const MOBILE_VIEW_GAP = 16;
const DESKTOP_VIEW_GAP = 64;
const DESKTOP_STATEMENT_WIDTH = 376;
const OFFSET_MARGIN_FOR_MOBILE = 20;
const PARTIAL_WIDTH_FOR_MOBILE = 20; // for prev and next statement

const Statement = React.forwardRef((props: StatementProps, ref) => {
    const {
        id,
        text,
        imageUrl,
        isMobileView,
        isActiveItem,
        statementWidth,
        isShowLottieGesture,
        carouselStatementPositionLabel,
    } = props;
    const recalledText = useRecall(text);
    const recalledImage = useRecall(imageUrl);
    const isRtl = useTextDirection() === "rtl";
    const theme: any = useTheme();
    const { ContentResponsivenessOption: contentResponsivenessOption } = theme;
    const imageWrapperRef = React.useRef<HTMLSpanElement | null>(null);
    const classes = useStyles({
        theme,
        isMobileView,
        isActiveItem,
        statementWidth,
        isShowLottieGesture,
        imageUrl: recalledImage,
        justifyContent: recalledImage ? "space-between" : "center",
    } as any);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    // Combine refs for forwarding
    const combinedRef = (element: HTMLDivElement | null) => {
        containerRef.current = element;
        if (ref) {
            // Only handle function refs, which are safer to use
            if (typeof ref === "function") {
                ref(element);
            }
            // Don't try to set the current property directly as it may be read-only
        }
    };

    const imageRef = React.useRef<HTMLImageElement | null>(null);
    const textContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [forceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        const container = containerRef.current;
        const image = imageRef.current;
        const wrapperContainer = imageWrapperRef.current;
        if (image) {
            image.addEventListener("load", responsiveRender);
        } else {
            resizeFont();
        }

        return () => {
            if (image) {
                image.removeEventListener("load", responsiveRender);
            }
        };

        function responsiveRender() {
            if (image && container && wrapperContainer) {
                resizeCarousel(image, container, wrapperContainer);
            }
            resizeFont();
        }

        function resizeFont() {
            if (container) {
                const fontSize = getFontSize(container);
                container.style.fontSize = `${fontSize}px`;
            }
        }
    }, [recalledImage, isMobileView, contentResponsivenessOption]);
    const isStatementHasContent = recalledText.replace("<br>", "").trim().length > 0;
    const statementPlainForAria = stripHtmlForAccessibleName(recalledText.replace("<br>", "").trim());
    const imageAltFromStatement =
        statementPlainForAria || (recalledImage ? "Statement image" : "");
    const slideContentName =
        statementPlainForAria.length > 0
            ? statementPlainForAria
            : recalledImage
              ? imageAltFromStatement
              : "";
    const slideAccessibleName =
        slideContentName.length > 0
            ? `${carouselStatementPositionLabel}. ${slideContentName}`
            : carouselStatementPositionLabel;
    const resizeCarousel = (
        image: HTMLImageElement,
        container: HTMLElement,
        wrapperContainer: HTMLElement,
    ) => {
        if (!image) return;
        const originalWidth = image.naturalWidth;
        const isSmallImage = statementWidth > originalWidth;
        const imageWidth = statementWidth < originalWidth ? "100%" : `${originalWidth}px`;
        image.style.width = imageWidth;
        container.style.width = `${statementWidth}px`;
        if (isSmallImage) {
            wrapperContainer.style.paddingTop = "16px";
        }
        if (isSmallImage && !isStatementHasContent) {
            wrapperContainer.style.paddingBottom = "16px";
        }
    };

    const getFontSize = (container: HTMLElement) => {
        const DEFAULT_FONT_SIZE = 16;
        const MINIMUM_FONT_SIZE = 12;
        const MAXIMUM_FONT_SIZE = 30;
        const containerWidth = container.offsetWidth;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) return DEFAULT_FONT_SIZE;

        context.font = `${DEFAULT_FONT_SIZE}px sans-serif`;
        const textWidth = context.measureText(recalledText).width;

        let fontSize = Math.floor((containerWidth / textWidth) * DEFAULT_FONT_SIZE);
        fontSize = Math.max(fontSize, MINIMUM_FONT_SIZE);
        fontSize = Math.min(fontSize, MAXIMUM_FONT_SIZE);

        if (textWidth < containerWidth && fontSize < DEFAULT_FONT_SIZE) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        if (contentResponsivenessOption === RESIZE_CAROUSEL) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        return fontSize;
    };

    const ClampedHTML = ({
        htmlContent,
        elementId,
    }: {
        htmlContent: string;
        elementId: string;
    }) => {
        return (
            <span
                className={isActiveItem ? "active-statement" : ""}
                id={elementId}
                style={{
                    display: "block",
                    WebkitLineClamp: "unset",
                    WebkitBoxOrient: "unset",
                    overflow: "hidden",
                    textOverflow: "clip",
                    wordWrap: "break-word",
                    lineHeight: "1.2em",
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
        );
    };

    const [touchStartX, setTouchStartX] = React.useState<number | null>(null);
    const [touchEndX, setTouchEndX] = React.useState<number | null>(null);
    const SWIPE_THRESHOLD = 20; // Minimum distance for a valid swipe

    React.useEffect(() => {
        const handleTouchStart = (e: TouchEvent) => {
            setTouchStartX(e.touches[0].clientX);
            setTouchEndX(null); // Reset touch end
        };

        const handleTouchMove = (e: TouchEvent) => {
            setTouchEndX(e.touches[0].clientX);
        };

        const handleTouchEnd = () => {
            if (touchStartX === null || touchEndX === null) return;

            const deltaX = touchStartX - touchEndX;
            if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
                // Touch coordinates are physical (unaffected by dir), but the next statement
                // sits on the opposite physical side under RTL (see computeCarrouselStatementTransform),
                // so which swipe direction means "next" flips too.
                const swipedLeft = deltaX > 0;
                const isNext = isRtl ? !swipedLeft : swipedLeft;
                props.handleSwipe(isNext ? "next" : "prev");
            }
            // Reset swipe values
            setTouchStartX(null);
            setTouchEndX(null);
        };

        const element = containerRef.current;
        if (element) {
            element.addEventListener("touchstart", handleTouchStart as EventListener);
            element.addEventListener("touchmove", handleTouchMove as EventListener);
            element.addEventListener("touchend", handleTouchEnd as EventListener);
        }

        return () => {
            if (element) {
                element.removeEventListener("touchstart", handleTouchStart as EventListener);
                element.removeEventListener("touchmove", handleTouchMove as EventListener);
                element.removeEventListener("touchend", handleTouchEnd as EventListener);
            }
        };
    }, [touchStartX, touchEndX, isRtl]);

    return (
        <div
            className={`${classes.statement} ${classes.statementShadow}`}
            style={{ boxShadow: resolveStatementBoxShadow(theme, isActiveItem) }}
            id={id}
            ref={combinedRef}
            tabIndex={-1}
            role="group"
            aria-label={slideAccessibleName}
            aria-roledescription="slide"
            aria-hidden={!isActiveItem}
        >
            {recalledImage && (
                <span className="image-wrapper" ref={imageWrapperRef}>
                    <img
                        className={`image-element`}
                        src={recalledImage}
                        ref={imageRef}
                        alt={imageAltFromStatement}
                    />
                </span>
            )}
            {isStatementHasContent && (
                <div className="statementTextContent" ref={textContainerRef} aria-hidden="true">
                    <ClampedHTML
                        elementId={`${id}_statementText`}
                        htmlContent={recalledText.trim()}
                        key={forceUpdateKey}
                    />
                </div>
            )}
        </div>
    );
});

interface WrapperWithNavigationButtonsProps {
    classes: any;
    isPrevBtnActive: boolean;
    isNextBtnActive: boolean;
    handleNavigationButtonClick: (direction: string) => void;
    backButtonText: string;
    nextButtonText: string;
    isNumberNavigation: boolean;
    carrouselActiveVariableIndex: number;
    sequencedVariables: any[];
    mentions: any;
    counterStatusAriaLabel: string;
    navigationAriaLabel: string;
    prevArrowRef: any;
    prevArrowOnKeyDown: (event: React.KeyboardEvent) => void;
    nextArrowRef: any;
    nextArrowOnKeyDown: (event: React.KeyboardEvent) => void;
}

const WrapperWithNavigationButtons = ({
    classes,
    isPrevBtnActive,
    isNextBtnActive,
    handleNavigationButtonClick,
    backButtonText,
    nextButtonText,
    isNumberNavigation,
    carrouselActiveVariableIndex,
    sequencedVariables,
    mentions,
    counterStatusAriaLabel,
    navigationAriaLabel,
    prevArrowRef,
    prevArrowOnKeyDown,
    nextArrowRef,
    nextArrowOnKeyDown,
}: WrapperWithNavigationButtonsProps) => {
    const isKeyboardNavigation = React.useRef(false);

    // Use refs to directly access DOM elements
    const prevArrowDomRef = React.useRef<HTMLDivElement | null>(null);
    const nextArrowDomRef = React.useRef<HTMLDivElement | null>(null);

    // Effect to ensure aria-hidden and tabindex stay synchronized
    React.useEffect(() => {
        // Update prev arrow tabindex to match active state
        if (prevArrowDomRef.current) {
            prevArrowDomRef.current.setAttribute("tabindex", isPrevBtnActive ? "0" : "-1");
        }

        // Update next arrow tabindex to match active state
        if (nextArrowDomRef.current) {
            nextArrowDomRef.current.setAttribute("tabindex", isNextBtnActive ? "0" : "-1");
        }
    }, [isPrevBtnActive, isNextBtnActive]);

    // Enhanced function to merge refs with proper preservation of the navigation ref
    const mergeRefs =
        (navigationRef: any, domRef: React.RefObject<HTMLDivElement>, isActive: boolean) =>
            (element: HTMLDivElement | null) => {
                // Update the DOM ref
                if (domRef.current !== element) {
                    (domRef as any).current = element;
                }

                // Only register with keyboard navigation if the button is active
                if (isActive) {
                    // This is critical: Call the function ref from useKeyboardNavigation
                    // to properly register this element in the keyboard navigation system
                    if (typeof navigationRef === "function") {
                        navigationRef(element);
                    } else if (navigationRef && "current" in navigationRef) {
                        // Support for object refs as well, though useKeyboardNavigation uses function refs
                        navigationRef.current = element;
                    }
                }
            };

    return (
        <div
            className={classes.navigationButtonsPanel}
            role="group"
            aria-label={navigationAriaLabel}
        >
            <div
                className="fixedWidth"
                role="button"
                ref={mergeRefs(prevArrowRef, prevArrowDomRef, isPrevBtnActive)}
                aria-disabled={!isPrevBtnActive}
                aria-hidden={!isPrevBtnActive}
                aria-label={backButtonText}
                onKeyDown={(event) => {
                    if (event.key !== " " && event.key !== "Enter") {
                        prevArrowOnKeyDown(event);
                    } else if (isPrevBtnActive) {
                        // Handle space/enter ourselves
                        isKeyboardNavigation.current = true;
                        handleNavigationButtonClick("prev");
                        event.preventDefault();
                    }
                }}
                onClick={() => handleNavigationButtonClick("prev")}
            >
                {isPrevBtnActive && (
                    <FontAwesomeIcon
                        icon={["fas", "chevron-left"]}
                        fixedWidth
                        className={classes.navigationButton}
                        aria-hidden={true}
                    />
                )}
            </div>

            {isNumberNavigation ? (
                <CounterNavigation
                    carrouselActiveVariableIndex={carrouselActiveVariableIndex}
                    totalLength={sequencedVariables.length}
                    statusAriaLabel={counterStatusAriaLabel}
                />
            ) : (
                <BulletNavigation
                    mentions={mentions}
                    carrouselActiveVariableIndex={carrouselActiveVariableIndex}
                    sequencedVariables={sequencedVariables}
                    classes={classes}
                />
            )}

            <div
                className="fixedWidth"
                role="button"
                ref={mergeRefs(nextArrowRef, nextArrowDomRef, isNextBtnActive)}
                aria-disabled={!isNextBtnActive}
                aria-hidden={!isNextBtnActive}
                aria-label={nextButtonText}
                onKeyDown={(event) => {
                    if (event.key !== " " && event.key !== "Enter") {
                        nextArrowOnKeyDown(event);
                    } else if (isNextBtnActive) {
                        // Handle space/enter ourselves
                        isKeyboardNavigation.current = true;
                        handleNavigationButtonClick("next");
                        event.preventDefault();
                    }
                }}
                onClick={() => handleNavigationButtonClick("next")}
            >
                {isNextBtnActive && (
                    <FontAwesomeIcon
                        icon={["fas", "chevron-right"]}
                        fixedWidth
                        className={classes.navigationButton}
                        aria-hidden={true}
                    />
                )}
            </div>
        </div>
    );
};

interface BulletNavigationProps {
    mentions: any;
    sequencedVariables: any[];
    carrouselActiveVariableIndex: number;
    classes: any;
}

interface ModernBulletIconProps {
    primaryColor: string;
    primaryForeground: string;
    pageBackground: string;
}

const ModernBulletIcon = ({
    state,
    colors,
}: {
    state: "default" | "answered" | "selected" | "selectedAnswered";
    colors: ModernBulletIconProps;
}) => {
    const { primaryColor, primaryForeground, pageBackground } = colors;

    if (state === "default") {
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
                <circle cx="8" cy="8" r="7.5" fill="none" stroke="#8a8a8a" strokeWidth="1" />
            </svg>
        );
    }

    if (state === "answered") {
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
                <circle cx="8" cy="8" r="7.5" fill={pageBackground} stroke="#8a8a8a" strokeWidth="1" />
                <svg
                    x="2"
                    y="2"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={primaryColor}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            </svg>
        );
    }

    if (state === "selected") {
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
                <circle cx="8" cy="8" r="7" fill={primaryColor} stroke={primaryColor} strokeWidth="2" />
            </svg>
        );
    }

    return (
        <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
            <circle cx="8" cy="8" r="7" fill={primaryColor} stroke={primaryColor} strokeWidth="2" />
            <svg
                x="2"
                y="2"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke={primaryForeground}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="20 6 9 17 4 12" />
            </svg>
        </svg>
    );
};

const BulletNavigation = ({
    mentions,
    sequencedVariables,
    carrouselActiveVariableIndex,
    classes,
}: BulletNavigationProps) => {
    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);
    const isRtl = useTextDirection() === "rtl";
    const modernBulletColors: ModernBulletIconProps = {
        primaryColor: theme.PrimaryColor,
        primaryForeground: theme.WhiteColor,
        pageBackground: theme.PageBackground,
    };
    const itemsToDisplay = 6;
    const showFirstEllipse =
        itemsToDisplay < sequencedVariables.length &&
        itemsToDisplay / 2 < carrouselActiveVariableIndex + 1;
    const showLastEllipse =
        itemsToDisplay < sequencedVariables.length &&
        carrouselActiveVariableIndex < sequencedVariables.length - 4;
    const fixedTransformValue = 25;
    const [transformValue, setTransformValue] = React.useState(0);
    React.useEffect(() => {
        let newTransformValue = transformValue;
        if (sequencedVariables.length > 6) {
            // Same physical/dir-mirror reasoning as computeCarrouselStatementTransform: negate
            // the LTR offset for RTL rather than recomputing it. Only branches that assign a
            // fresh value are negated — the untouched carry-over path below (no condition
            // matches) must keep returning the current state unchanged, or negating it every
            // render would fight setTransformValue into an infinite update loop.
            if (
                carrouselActiveVariableIndex > 2 &&
                sequencedVariables.length - carrouselActiveVariableIndex > 3
            ) {
                const ltrValue = -fixedTransformValue * (carrouselActiveVariableIndex - 2);
                newTransformValue = isRtl ? -ltrValue : ltrValue;
            } else if (carrouselActiveVariableIndex < 3) {
                newTransformValue = 0;
            } else if (newTransformValue == 0 && carrouselActiveVariableIndex > itemsToDisplay) {
                const ltrValue = -fixedTransformValue * (sequencedVariables.length - itemsToDisplay);
                newTransformValue = isRtl ? -ltrValue : ltrValue;
            }
            if (newTransformValue !== transformValue) {
                setTransformValue(newTransformValue); // Update state only if there's a change
            }
        }
    });
    return (
        <div className={classes.bulletNavigationPanel} aria-hidden={true}>
            <div className="ellipse-item">{showFirstEllipse && <div></div>}</div>
            <div className="bullets-container">
                {sequencedVariables.map((variable: any, index: number) => {
                    const activeItem = carrouselActiveVariableIndex === index;
                    const answeredItem =
                        mentions.hasOwnProperty(variable.name) && mentions[variable.name].length;
                    const className =
                        useModernTheme
                            ? ""
                            : activeItem && answeredItem
                                ? "active-answered-item"
                                : activeItem && !answeredItem
                                    ? "active-not-answered-item"
                                    : "";
                    return (
                        <div
                            key={index}
                            style={{
                                transform: `translateX(${transformValue}px)`,
                            }}
                            className={classnames(className, classes.bulletIndicatorItem)}
                        >
                            {useModernTheme ? (
                                <ModernBulletIcon
                                    state={
                                        activeItem && answeredItem
                                            ? "selectedAnswered"
                                            : answeredItem && !activeItem
                                              ? "answered"
                                              : activeItem && !answeredItem
                                                ? "selected"
                                                : "default"
                                    }
                                    colors={modernBulletColors}
                                />
                            ) : activeItem && answeredItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                >
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="17"
                                        height="17"
                                        rx="8.5"
                                        stroke="white"
                                    />
                                    <path
                                        d="M9 17C13.4187 17 17 13.4187 17 9C17 4.58125 13.4187 1 9 1C4.58125 1 1 4.58125 1 9C1 13.4187 4.58125 17 9 17ZM12.5312 7.53125L8.53125 11.5312C8.2375 11.825 7.7625 11.825 7.47188 11.5312L5.47188 9.53125C5.17813 9.2375 5.17813 8.7625 5.47188 8.47188C5.76562 8.18125 6.24062 8.17813 6.53125 8.47188L8 9.94063L11.4688 6.46875C11.7625 6.175 12.2375 6.175 12.5281 6.46875C12.8187 6.7625 12.8219 7.2375 12.5281 7.52812L12.5312 7.53125Z"
                                        fill="#316AEE"
                                    />
                                </svg>
                            ) : answeredItem && !activeItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                >
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="17"
                                        height="17"
                                        rx="8.5"
                                        stroke="white"
                                    />
                                    <path
                                        d="M9 17C13.4187 17 17 13.4187 17 9C17 4.58125 13.4187 1 9 1C4.58125 1 1 4.58125 1 9C1 13.4187 4.58125 17 9 17ZM12.5312 7.53125L8.53125 11.5312C8.2375 11.825 7.7625 11.825 7.47188 11.5312L5.47188 9.53125C5.17813 9.2375 5.17813 8.7625 5.47188 8.47188C5.76562 8.18125 6.24062 8.17813 6.53125 8.47188L8 9.94063L11.4688 6.46875C11.7625 6.175 12.2375 6.175 12.5281 6.46875C12.8187 6.7625 12.8219 7.2375 12.5281 7.52812L12.5312 7.53125Z"
                                        fill="#316AEE"
                                    />
                                </svg>
                            ) : activeItem && !answeredItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        fill="#316AEE"
                                        stroke="white"
                                        strokeWidth="2"
                                    />
                                </svg>
                            ) : (
                                <div className="bullet" aria-hidden={true} />
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="ellipse-item">{showLastEllipse && <div></div>}</div>
        </div>
    );
};

interface CounterNavigationProps {
    carrouselActiveVariableIndex: number;
    totalLength: number;
    statusAriaLabel: string;
}

const CounterNavigation = ({
    carrouselActiveVariableIndex,
    totalLength,
    statusAriaLabel,
}: CounterNavigationProps) => {
    return (
        <div className="counter-navigation" role="status" aria-label={statusAriaLabel}>{`${carrouselActiveVariableIndex + 1}/${totalLength}`}</div>
    );
};

export function CarrouselControl(props: ControlProps) {
    const {
        question,
        sequencedVariables,
        validateSignal,
        mentions,
        questionnaire,
        isDesktopPreviewView,
        showSoftPrompt,
    } = props;
    const questionHasAtleastOneVariable = sequencedVariables && sequencedVariables.length > 0;

    // This condition only occurs if all the statements is eliminated then only question description is displayed
    if (!questionHasAtleastOneVariable) {
        return "";
    }

    const instanceId = `${question.id}_statement`;
    const isNumberNavigation = question.settings.NavigationStyle != "Bullets";
    let hideVariableBackArrow = false;
    let canLoopVariable = false;
    let advanceAutomatically = false;
    let activeStatementMentionsCount = 0;
    const questionName = question.name;
    const dispatch = useDispatch();
    const isPrevBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel?.[instanceId]?.isPrevBtnActive || false,
    );
    const isNextBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel?.[instanceId]?.isNextBtnActive || false,
    );
    const carrouselActiveVariableIndex = useSelector(
        (state: StoreState) => state.interview.carrousel?.[instanceId]?.activeVariableIndex || 0,
    );

    // Create a variable object for keyboard navigation
    const AbovePreArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_above_pre`,
            name: "carrouselVarAbovePrev",
            id: `${instanceId}_var_above_pre`,
        }),
        [instanceId, question.id],
    );

    const AboveNextArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_above_next`,
            name: "carrouselVarAboveNext",
            id: `${instanceId}_var_above_next`,
        }),
        [instanceId, question.id],
    );

    const BelowPrevArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_below_pre`,
            name: "carrouselVarBelowPrev",
            id: `${instanceId}_var_below_pre`,
        }),
        [instanceId, question.id],
    );

    const BelowNextArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_below_next`,
            name: "carrouselVarBelowNext",
            id: `${instanceId}_var_below_next`,
        }),
        [instanceId, question.id],
    );

    // Get active variable info (rename this version to avoid conflicts with the one below)
    const currentActiveCarouselVariable = sequencedVariables[carrouselActiveVariableIndex];
    const currentCarouselVariableName = currentActiveCarouselVariable?.name || "";

    // Add a ref to track previous active variable
    const prevActiveVariableRef = React.useRef(currentCarouselVariableName);

    // Setup keyboard navigation for prev/next arrows with active variable name in ID
    // Register prev arrow first to match natural left-to-right reading order
    const { elementReference: abovePrevArrowRef, onKeyDown: abovePrevArrowOnKeyDown } =
        useKeyboardNavigation(
            `Above_CarrouselPrevArrow_${currentCarouselVariableName}`,
            AbovePreArrowVariable,
            question,
            CarrouselNavigation,
            () => handleNavigationButtonClick("prev"),
        );

    const { elementReference: aboveNextArrowRef, onKeyDown: aboveNextArrowOnKeyDown } =
        useKeyboardNavigation(
            `Above_CarrouselNextArrow_${currentCarouselVariableName}`,
            AboveNextArrowVariable,
            question,
            CarrouselNavigation,
            () => handleNavigationButtonClick("next"),
        );

    const { elementReference: belowPrevArrowRef, onKeyDown: belowPrevArrowOnKeyDown } =
        useKeyboardNavigation(
            `Below_CarrouselPrevArrow_${currentCarouselVariableName}`,
            BelowPrevArrowVariable,
            question,
            CarrouselNavigation,
            () => handleNavigationButtonClick("prev"),
        );

    const { elementReference: belowNextArrowRef, onKeyDown: belowNextArrowOnKeyDown } =
        useKeyboardNavigation(
            `Below_CarrouselNextArrow_${currentCarouselVariableName}`,
            BelowNextArrowVariable,
            question,
            CarrouselNavigation,
            () => handleNavigationButtonClick("next"),
        );

    // Register statement refs for keyboard navigation
    const statementRefs = React.useRef<React.RefObject<HTMLDivElement>[]>([]);
    const defaultFocusStatus = useSelector((state: StoreState) => state.defaultFocusStatus);
    const carouselVariableIds = React.useMemo(
        () => new Set(sequencedVariables.map((variable) => variable.id)),
        [sequencedVariables],
    );
    const carouselVariableNames = React.useMemo(
        () => sequencedVariables.map((variable) => variable.name).filter(Boolean),
        [sequencedVariables],
    );
    const carouselArrowVariableIds = React.useMemo(
        () =>
            new Set([
                Number(AbovePreArrowVariable.id),
                Number(AboveNextArrowVariable.id),
                Number(BelowPrevArrowVariable.id),
                Number(BelowNextArrowVariable.id),
            ]),
        [
            AbovePreArrowVariable.id,
            AboveNextArrowVariable.id,
            BelowPrevArrowVariable.id,
            BelowNextArrowVariable.id,
        ],
    );
    const isCarouselChoiceElementId = React.useCallback((elementId: string) => {
        return elementId.includes("_C_") || elementId.includes("_INPUT");
    }, []);
    const isCarouselOwnedStatus = React.useCallback(
        (status: any) => {
            if (!status || status.questionId !== question.id) return false;
            const elementId = status.elementId?.toString() || "";
            const variableId = Number(status.variableId);
            const isArrowElement =
                carouselArrowVariableIds.has(variableId) &&
                (elementId.includes("CarrouselPrevArrow") || elementId.includes("CarrouselNextArrow"));
            if (isArrowElement) return true;

            const isCarouselVariable = carouselVariableIds.has(variableId);
            if (!isCarouselVariable) return false;

            const hasKnownVariableName = carouselVariableNames.some((name) => elementId.includes(name));
            const isStatementElement = elementId.includes(`${instanceId}_statement`);
            const isChoiceElement = isCarouselChoiceElementId(elementId);
            return hasKnownVariableName || isStatementElement || isChoiceElement;
        },
        [
            question.id,
            carouselArrowVariableIds,
            carouselVariableIds,
            carouselVariableNames,
            instanceId,
            isCarouselChoiceElementId,
        ],
    );

    // Initialize refs array for statements
    React.useEffect(() => {
        statementRefs.current = sequencedVariables.map(() => React.createRef<HTMLDivElement>());
    }, [sequencedVariables.length]);

    React.useEffect(() => {
        if (!defaultFocusStatus?.statuses?.length) return;

        const carouselElements = [];
        const carouselIndices = [];

        for (let i = 0; i < defaultFocusStatus.statuses.length; i++) {
            const status = defaultFocusStatus.statuses[i];
            const elementId = status.elementId?.toString() || "";

            if (
                isCarouselOwnedStatus(status) &&
                (elementId.includes(currentCarouselVariableName) ||
                    elementId.includes(`CarrouselPrevArrow_${currentCarouselVariableName}`) ||
                    elementId.includes(`CarrouselNextArrow_${currentCarouselVariableName}`))
            ) {
                carouselElements.push(status);
                carouselIndices.push(i);
            }
        }

        if (carouselElements.length > 1) {
            const firstCarouselIndex = Math.min(...carouselIndices);

            const aboveArrowElements = [];
            const belowArrowElements = [];
            const statementElements: any[] = [];
            const activeChoiceElementsFirst: any[] = [];
            const otherChoiceElementsFirst: any[] = [];
            const activeVariableNameForOrder =
                sequencedVariables[carrouselActiveVariableIndex]?.name;

            for (const element of carouselElements) {
                const elementId = element.elementId?.toString() || "";
                if (
                    elementId.includes("Above_CarrouselPrevArrow") ||
                    elementId.includes("Above_CarrouselNextArrow")
                ) {
                    aboveArrowElements.push(element);
                } else if (
                    elementId.includes("Below_CarrouselPrevArrow") ||
                    elementId.includes("Below_CarrouselNextArrow")
                ) {
                    belowArrowElements.push(element);
                } else if (isCarouselChoiceElementId(elementId)) {
                    if (activeVariableNameForOrder && elementId.includes(activeVariableNameForOrder)) {
                        activeChoiceElementsFirst.push(element);
                    } else {
                        otherChoiceElementsFirst.push(element);
                    }
                } else {
                    statementElements.push(element);
                }
            }

            const nonArrowElements = [
                ...statementElements,
                ...activeChoiceElementsFirst,
                ...otherChoiceElementsFirst,
            ];
            const allArrowElements = [...aboveArrowElements, ...belowArrowElements];
            if (allArrowElements.length && nonArrowElements.length) {
                const arrowIndices = allArrowElements.map((e) =>
                    defaultFocusStatus.statuses.indexOf(e),
                );
                const nonArrowIndices = nonArrowElements.map((e) =>
                    defaultFocusStatus.statuses.indexOf(e),
                );

                const minArrowIndex = Math.min(...arrowIndices);
                const minNonArrowIndex = Math.min(...nonArrowIndices);

                if (
                    minNonArrowIndex < minArrowIndex ||
                    !arrowIndices.every((_, i, arr) => i === 0 || arr[i - 1] <= arr[i])
                ) {
                    // Remove all carousel elements
                    for (let i = defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                        if (carouselIndices.includes(i)) {
                            defaultFocusStatus.statuses.splice(i, 1);
                        }
                    }

                    // Reorder to match DOM: statements → above nav → choices → below nav
                    const reordered = [
                        ...statementElements,
                        ...aboveArrowElements,
                        ...activeChoiceElementsFirst,
                        ...otherChoiceElementsFirst,
                        ...belowArrowElements,
                    ];
                    defaultFocusStatus.statuses.splice(firstCarouselIndex, 0, ...reordered);

                    // Dispatch the update
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            }
        }
    }, [
        defaultFocusStatus,
        currentCarouselVariableName,
        dispatch,
        isCarouselOwnedStatus,
        sequencedVariables,
        carrouselActiveVariableIndex,
        isCarouselChoiceElementId,
    ]);

    // Simple effect to replace variable names while preserving positions
    React.useEffect(() => {
        if (!defaultFocusStatus || !defaultFocusStatus.statuses) return;

        const prevVariableName = prevActiveVariableRef.current;

        // Only process if previous variable exists and is different from current
        if (prevVariableName && prevVariableName !== currentCarouselVariableName) {
            let updated = false;

            // Find current variable ID from sequencedVariables
            const currentVarObj = sequencedVariables.find(
                (v) => v.name === currentCarouselVariableName,
            );
            const prevVarObj = sequencedVariables.find((v) => v.name === prevVariableName);

            if (currentVarObj?.id && prevVarObj?.id) {
                // Loop through all elements and replace variable names in place
                for (let i = 0; i < defaultFocusStatus.statuses.length; i++) {
                    const status = defaultFocusStatus.statuses[i];
                    const elementId = status.elementId ? status.elementId.toString() : "";

                    // Carousel arrows - check BOTH pattern AND variableId
                    const isCarouselArrow =
                        (status.variableId.toString() === AbovePreArrowVariable.id ||
                            status.variableId.toString() === AboveNextArrowVariable.id ||
                            status.variableId.toString() == BelowPrevArrowVariable.id ||
                            status.variableId.toString() == BelowNextArrowVariable.id) &&
                        (elementId.includes(`CarrouselPrevArrow_${prevVariableName}`) ||
                            elementId.includes(`CarrouselNextArrow_${prevVariableName}`));

                    // Carousel statements - check by instance ID pattern
                    const isCarouselStatement =
                        elementId.includes(`${instanceId}_statement`) &&
                        elementId.includes(prevVariableName);

                    // Choice elements - check by variable ID
                    const isChoiceElement =
                        status.variableId === prevVarObj.id &&
                        (elementId.includes("_C_") || elementId.includes("_INPUT"));

                    // Only update elements that match our criteria
                    if (isCarouselArrow || isCarouselStatement || isChoiceElement) {
                        // Replace the variable name in the element ID
                        defaultFocusStatus.statuses[i].elementId = elementId.replace(
                            prevVariableName,
                            currentCarouselVariableName,
                        );

                        // Update variableId for choice elements only
                        if (isChoiceElement) {
                            defaultFocusStatus.statuses[i].variableId = currentVarObj.id;
                        }

                        updated = true;
                    }
                }

                // Update Redux store only if changes were made
                if (updated) {
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            }
        }

        // Update ref for next comparison
        prevActiveVariableRef.current = currentCarouselVariableName;
    }, [
        currentCarouselVariableName,
        defaultFocusStatus,
        dispatch,
        sequencedVariables,
        AbovePreArrowVariable.id,
        AboveNextArrowVariable.id,
        BelowPrevArrowVariable.id,
        BelowNextArrowVariable.id,
        instanceId,
    ]);

    React.useEffect(() => {
        if (!defaultFocusStatus?.statuses?.length) return;

        const carouselIndices: number[] = [];
        defaultFocusStatus.statuses.forEach((status: any, index: number) => {
            if (isCarouselOwnedStatus(status)) {
                carouselIndices.push(index);
            }
        });

        if (!carouselIndices.length) return;

        const activeVariableName = sequencedVariables[carrouselActiveVariableIndex]?.name;

        const buildReorderedCarouselBlock = (carouselBlock: any[]) => {
            const aboveArrowElements: any[] = [];
            const belowArrowElements: any[] = [];
            const activeChoiceElements: any[] = [];
            const otherChoiceElements: any[] = [];
            const nonChoiceElements: any[] = [];

            carouselBlock.forEach((status: any) => {
                const elementId = status.elementId?.toString() || "";
                if (
                    elementId.includes("Above_CarrouselPrevArrow") ||
                    elementId.includes("Above_CarrouselNextArrow")
                ) {
                    aboveArrowElements.push(status);
                    return;
                }

                if (
                    elementId.includes("Below_CarrouselPrevArrow") ||
                    elementId.includes("Below_CarrouselNextArrow")
                ) {
                    belowArrowElements.push(status);
                    return;
                }

                if (isCarouselChoiceElementId(elementId)) {
                    if (activeVariableName && elementId.includes(activeVariableName)) {
                        activeChoiceElements.push(status);
                    } else {
                        otherChoiceElements.push(status);
                    }
                    return;
                }

                nonChoiceElements.push(status);
            });

            // Match DOM order: statements strip → above nav (if any) → choices → below nav (if any).
            // See render: statementsContainer, then optional above WrapperWithNavigationButtons,
            // then choiceAndNavigationContainer (choices, then optional below nav).
            return [
                ...nonChoiceElements,
                ...aboveArrowElements,
                ...activeChoiceElements,
                ...otherChoiceElements,
                ...belowArrowElements,
            ];
        };

        const sortedByIndex = [...carouselIndices].sort((a, b) => a - b);
        const isContiguous = sortedByIndex.every(
            (currentIndex, index) => index === 0 || currentIndex === sortedByIndex[index - 1] + 1,
        );

        const carouselBlock = sortedByIndex.map((index) => defaultFocusStatus.statuses[index]);
        const reordered = buildReorderedCarouselBlock(carouselBlock);
        const insertAt = sortedByIndex[0];

        if (isContiguous) {
            const isSameOrder = reordered.every(
                (status, index) => status === defaultFocusStatus.statuses[insertAt + index],
            );
            if (isSameOrder) return;

            defaultFocusStatus.statuses.splice(insertAt, reordered.length, ...reordered);
            dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
            return;
        }

        // Non-contiguous: choices/arrows registered out of order — pull all carousel-owned
        // entries into one block so Tab order does not leave nav above/below unreachable
        // or place choices after unrelated questions.
        const sortedDescending = [...carouselIndices].sort((a, b) => b - a);
        for (const idx of sortedDescending) {
            defaultFocusStatus.statuses.splice(idx, 1);
        }
        defaultFocusStatus.statuses.splice(insertAt, 0, ...reordered);
        dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
    }, [
        defaultFocusStatus,
        dispatch,
        carrouselActiveVariableIndex,
        sequencedVariables,
        isCarouselOwnedStatus,
        isCarouselChoiceElementId,
    ]);

    if (hasSetting(question, "HideVariableBackArrow")) {
        hideVariableBackArrow = getSettingBoolean(question, "HideVariableBackArrow");
    }

    if (hasSetting(question, "LoopVariable")) {
        canLoopVariable = getSettingBoolean(question, "LoopVariable");
    }

    if (hasSetting(question, "AutoAdvanceVariable")) {
        advanceAutomatically = getSettingBoolean(question, "AutoAdvanceVariable");
    }


    const backButtonAlwaysVisible = canLoopVariable && !hideVariableBackArrow;
    const [language] = useLanguage();
    const isRtl = useTextDirection() === "rtl";
    const theme = useTheme();
    const counter = React.useRef(1);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [deviceWidth] = React.useState<number>(window.innerWidth);
    const [deviceHeight] = React.useState<number>(window.innerHeight);
    const mobilePreview = useSelector((state: StoreState) => state.mobilePreview);
    const isMobileView = !isDesktopPreviewView && (deviceWidth <= MOBILE_WIDTH || mobilePreview);
    const totalSlideCount = sequencedVariables.length;
    const slidePosition1Based = Math.min(
        Math.max(carrouselActiveVariableIndex + 1, 1),
        Math.max(totalSlideCount, 1),
    );
    const carouselActivePositionLabel = React.useMemo(
        () => resolveCarouselAriaLabel(
            questionnaire.theme,
            "CarouselAriaCurrentStatement",
            language,
            slidePosition1Based,
            totalSlideCount,
            "Statement {current} of {total}",
        ),
        [questionnaire.theme, language, slidePosition1Based, totalSlideCount],
    );
    const backButtonWithPosition =
        translate(questionnaire.theme, "CarouselAriaPrev", language) ||
        "Prev";
    const nextButtonWithPosition =
        translate(questionnaire.theme, "CarouselAriaNext", language) ||
        "Next";
    const carouselRegionAriaLabel = React.useMemo(
        () => getCarouselRegionAriaLabel(question, language),
        [question, language],
    );
    const carouselLiveAnnouncement = React.useMemo(
        () => resolveCarouselAriaLabel(
            questionnaire.theme,
            "CarouselAriaStatementTransition",
            language,
            carrouselActiveVariableIndex + 1,
            sequencedVariables.length,
            "Showing Statement {current} of {total}",
        ),
        [questionnaire.theme, language, carrouselActiveVariableIndex, sequencedVariables.length],
    );
    const [liveAnnouncementText, setLiveAnnouncementText] = React.useState("");
    const hasAnnouncedQuestionContextRef = React.useRef(false);
    const prevSlideIndexRef = React.useRef(carrouselActiveVariableIndex);
    const liveAnnouncementTimerRef = React.useRef<number | undefined>(undefined);
    const carouselNavigationAriaLabel =
        translate(questionnaire.theme, "CarouselAriaNavigation", language) || "Carousel navigation";

    const queueLiveAnnouncement = React.useCallback((text: string) => {
        if (liveAnnouncementTimerRef.current != null) {
            window.clearTimeout(liveAnnouncementTimerRef.current);
        }
        setLiveAnnouncementText("");
        liveAnnouncementTimerRef.current = window.setTimeout(() => {
            liveAnnouncementTimerRef.current = undefined;
            setLiveAnnouncementText(text);
        }, 150);
    }, []);

    React.useEffect(() => {
        hasAnnouncedQuestionContextRef.current = false;
        prevSlideIndexRef.current = carrouselActiveVariableIndex;
        setLiveAnnouncementText("");
        return () => {
            if (liveAnnouncementTimerRef.current != null) {
                window.clearTimeout(liveAnnouncementTimerRef.current);
            }
        };
    }, [question.id]);

    const announceQuestionContextOnce = React.useCallback(() => {
        if (hasAnnouncedQuestionContextRef.current || !carouselRegionAriaLabel) {
            return;
        }
        hasAnnouncedQuestionContextRef.current = true;
        queueLiveAnnouncement(carouselRegionAriaLabel);
    }, [carouselRegionAriaLabel, queueLiveAnnouncement]);

    const handleCarouselFocusIn = React.useCallback(() => {
        announceQuestionContextOnce();
    }, [announceQuestionContextOnce]);

    const handleCarouselFocusOut = React.useCallback((event: React.FocusEvent) => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const nextFocused = event.relatedTarget;
        if (nextFocused instanceof Node && container.contains(nextFocused)) {
            return;
        }
        hasAnnouncedQuestionContextRef.current = false;
    }, []);

    React.useEffect(() => {
        if (prevSlideIndexRef.current === carrouselActiveVariableIndex) {
            return;
        }
        prevSlideIndexRef.current = carrouselActiveVariableIndex;
        queueLiveAnnouncement(carouselLiveAnnouncement);
    }, [carrouselActiveVariableIndex, carouselLiveAnnouncement, queueLiveAnnouncement]);
    const isAboveChoiceNavigation = question.settings?.NavigationDisplay?.includes("Above") ?? true;
    const isBelowChoiceNavigation =
        question.settings?.NavigationDisplay?.includes("Below") ?? false;
    const choiceListDisplayType = getChoiceListDisplayType(question);
    // const justifyContent = isMobileView ? "space-between" : "center";
    const classes = useStyles({
        theme: theme as any,
        isMobileView,
        isAboveChoiceNavigation,
        choiceListDisplayType,
        isRtl,
    } as any);
    const [statementValidation, setStatementValidationCheck] = React.useState({
        action: "NONE",
        direction: "NONE",
    });
    const [directionSaved, setDirection] = React.useState("NONE");
    const errorMonitor = useSelector(
        (state: StoreState) => state.interview.validation.errorsByVariableName,
    );
    let prevErrorMonitorRef = React.useRef(null);
    const [isCarouselVisible, setIsCarouselVisible] = React.useState(false);
    const [isFirstTimeRender, setIsFirstTimeRender] = React.useState(true);
    const [showLottieGesture, setShowLottieGesture] = React.useState(true);
    const isShowLottieGesture = showLottieGesture && isMobileView && isCarouselVisible;

    React.useEffect(() => {
        const questionContainerHeight = containerRef.current?.offsetHeight;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (isMobileView) {
                    setIsCarouselVisible(entry.isIntersecting); // Update visibility state
                }
            },
            {
                root: null, // Use the viewport as the root
                threshold: (questionContainerHeight || 0) > deviceHeight ? 0.5 : 0.9, // Trigger based on viewport
            },
        );
        if (containerRef.current) {
            observer.observe(containerRef.current); // Start observing the carousel container
        }
        // Cleanup the observer on component unmount
        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    React.useEffect(() => {
        if (isCarouselVisible) {
            if (isFirstTimeRender) {
                setIsFirstTimeRender(false);
                if (showLottieGesture) {
                    setTimeout(() => {
                        setShowLottieGesture(false);
                    }, 2000);
                }
            }
        }
    }, [isCarouselVisible]);
    // This condition occurs if due to statement elimination some variable got removed and greater active index is previously stored
    if (!currentActiveCarouselVariable) {
        if (questionHasAtleastOneVariable && carrouselActiveVariableIndex !== 0) {
            // dispatch will be triggered with some delay then exception so setting active variable here
            dispatch(
                setCarrouselActiveVariableIndex(
                    instanceId,
                    0,
                    backButtonAlwaysVisible,
                    canLoopVariable || counter.current !== sequencedVariables.length,
                ),
            );
        } else {
            return "";
        }
    }

    const variableHasChoices =
        currentActiveCarouselVariable &&
        currentActiveCarouselVariable.choiceList &&
        currentActiveCarouselVariable.choiceList.length > 0;
    const variableName = currentActiveCarouselVariable?.name || "";
    const isSoftPrompt = isQuestionSoftPrompt(question);
    const isRequired = isAnswerRequired(question, questionnaire) && !isSoftPrompt;
    const defaultMinRequiredAnswerValue = 1;

    const getMinRequiredAnswers = React.useCallback(() => {
        const number = getSettingNumber(question, "MinRequiredAnswers");
        if (number !== undefined && !Number.isNaN(number)) {
            return number === 0 ? defaultMinRequiredAnswerValue : number;
        }
        return null;
    }, [question, defaultMinRequiredAnswerValue]);

    const minRequiredAnswers = getMinRequiredAnswers();

    if (
        mentions &&
        Object.keys(mentions).length > 0 &&
        mentions[variableName] &&
        mentions[variableName].length > 0
    ) {
        activeStatementMentionsCount = mentions[variableName].length;
    }
    const activeStatementHasRequiredMentions =
        activeStatementMentionsCount >= (minRequiredAnswers ?? defaultMinRequiredAnswerValue);

    const setValidationError = React.useCallback(
        (errorKey: string, minChoices: number) => {
            const activeVariableName =
                sequencedVariables[carrouselActiveVariableIndex]?.name ?? variableName;
            const errorEntry = [
                {
                    key: errorKey,
                    softPrompt: showSoftPrompt,
                    args: { min: minChoices.toString() },
                },
            ];
            const questionValidationError = {
                [questionName]: errorEntry,
                ...(activeVariableName ? { [activeVariableName]: errorEntry } : {}),
            };

            dispatch(
                validationSetResult({
                    errorsByVariableName: questionValidationError,
                    isValid: false,
                }),
            );
        },
        [
            carrouselActiveVariableIndex,
            dispatch,
            questionName,
            sequencedVariables,
            showSoftPrompt,
            variableName,
        ],
    );

    React.useEffect(() => {
        if (isSoftPrompt) {
            if (JSON.stringify(prevErrorMonitorRef.current) === JSON.stringify(errorMonitor)) {
                return;
            }

            prevErrorMonitorRef.current = errorMonitor as any;

            if (
                !errorMonitor ||
                !question ||
                !sequencedVariables ||
                sequencedVariables.length === 0
            ) {
                return;
            }
            if (errorMonitor.hasOwnProperty(question.name)) {
                const errors = errorMonitor[question.name];
                if (Array.isArray(errors) && errors.length > 0) {
                    const lastError = errors[errors.length - 1];
                    const errorVariableIndex = sequencedVariables.findIndex(
                        (variable) => variable.name === lastError.varInfo,
                    );
                    counter.current = errorVariableIndex + 1;
                    const newPrevBtnActive = backButtonAlwaysVisible || errorVariableIndex > 0;
                    const newNextBtnActive =
                        canLoopVariable || errorVariableIndex < sequencedVariables.length - 1;
                    dispatch(
                        setCarrouselActiveVariableIndex(
                            instanceId,
                            errorVariableIndex,
                            newPrevBtnActive,
                            newNextBtnActive,
                        ),
                    );
                }
            }
        }
    }, [errorMonitor]);

    React.useEffect(() => {
        if (statementValidation.action === "VALIDATEMENTIONS") {
            if (activeStatementHasRequiredMentions) {
                scrollHandler(statementValidation.direction, sequencedVariables);
                setStatementValidationCheck({
                    action: "MENTIONSVALIDATEDSUCCESSFULLY",
                    direction: "NONE",
                });
            } else {
                setStatementValidationCheck({
                    action: "SHOWMENTIONERROR",
                    direction: "NONE",
                });

                const errorKey =
                    activeStatementMentionsCount === 0 ? "IncompleteAnswer" : "InvalidChoicesMin";

                const minChoices = minRequiredAnswers ?? defaultMinRequiredAnswerValue;

                setValidationError(errorKey, minChoices);
            }
        }
    }, [statementValidation]);

    React.useEffect(() => {
        const index = counter.current - 1;
        const canLoop = canLoopVariable || counter.current !== sequencedVariables.length;

        dispatch(
            setCarrouselActiveVariableIndex(instanceId, index, backButtonAlwaysVisible, canLoop),
        );
    }, []); // Runs once on component mount

    const scrollHandler = (direction: string, vars: any[]) => {
        const isPrev = direction === "prev";

        // eslint-disable-next-line no-nested-ternary
        counter.current = isPrev
            ? canLoopVariable && counter.current === 1
                ? vars.length
                : counter.current - 1
            : canLoopVariable && counter.current === vars.length
                ? 1
                : Math.min(counter.current + 1, sequencedVariables.length);

        const newPrevBtnActive = isPrev
            ? backButtonAlwaysVisible || counter.current > 1
            : !hideVariableBackArrow;

        const newNextBtnActive = canLoopVariable || counter.current !== sequencedVariables.length;
        if (directionSaved !== direction) {
            setDirection(direction);
        }
        dispatch(
            setCarrouselActiveVariableIndex(
                instanceId,
                counter.current - 1,
                newPrevBtnActive,
                newNextBtnActive,
            ),
        );
    };

    const handleNavigationButtonClick = React.useCallback(
        (direction: string, isAfterSelectionAction = false) => {
            // If no choices is there then no validation check on arrow click (Can be due to elimination process)
            if (variableHasChoices) {
                const notRequiredButMinChoiceIsDefinedValidation =
                    !isRequired &&
                    minRequiredAnswers &&
                    activeStatementMentionsCount !== 0 &&
                    !activeStatementHasRequiredMentions;

                // For automatically advance(radio only) and prev button click move to next statement
                if (isAfterSelectionAction || (direction === "prev" && !canLoopVariable)) {
                    scrollHandler(direction, sequencedVariables);
                } else if (isRequired || notRequiredButMinChoiceIsDefinedValidation) {
                    setStatementValidationCheck({
                        action: "VALIDATEMENTIONS",
                        direction,
                    });
                } else {
                    scrollHandler(direction, sequencedVariables);
                }
            } else {
                scrollHandler(direction, sequencedVariables);
            }
        },
        [
            isRequired,
            sequencedVariables,
            minRequiredAnswers,
            activeStatementMentionsCount,
            activeStatementHasRequiredMentions,
            variableHasChoices,
        ],
    );

    const onAfterCarouselChoiceSelection = React.useCallback(
        (isAfterSelectionAction: boolean) => {
            if (advanceAutomatically && isCarrouselRadioLikeMode(question)) {
                if (sequencedVariables.length - 1 > carrouselActiveVariableIndex) {
                    // In test environment, execute immediately for reliable testing
                    if (process.env.NODE_ENV === 'test') {
                        handleNavigationButtonClick("next", isAfterSelectionAction);

                        if (containerRef.current?.offsetParent instanceof Element) {
                            const offsetParent = containerRef.current.offsetParent as HTMLElement;
                            offsetParent.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                    } else {
                        setTimeout(() => {
                            handleNavigationButtonClick("next", isAfterSelectionAction);

                            if (containerRef.current?.offsetParent instanceof Element) {
                                const offsetParent = containerRef.current.offsetParent as HTMLElement;
                                offsetParent.scrollIntoView({ behavior: "smooth", block: "start" });
                            }
                        }, 300);
                    }
                }
            }
        },
        [
            sequencedVariables,
            carrouselActiveVariableIndex,
            advanceAutomatically,
            question,
            handleNavigationButtonClick,
        ],
    );

    useValidation(validateSignal!, useDiscreteVariadicValidator(question, sequencedVariables));

    const commonProps = {
        isNextBtnActive,
        isPrevBtnActive,
        handleNavigationButtonClick,
        classes,
        carrouselActiveVariableIndex,
        isNumberNavigation,
        sequencedVariables,
        mentions,
        counterStatusAriaLabel: carouselActivePositionLabel,
        navigationAriaLabel: carouselNavigationAriaLabel,
    };

    const gap = isMobileView ? MOBILE_VIEW_GAP : DESKTOP_VIEW_GAP;
    const borderWidth = resolveCarrouselCornerBorderWidth(theme as Theme);
    const noOfSides = 2; // both side value i.e left and right
    const bordersTotalWidth = noOfSides * borderWidth;
    let statementWidth;
    if (isMobileView && deviceWidth < SMALL_WIDTH) {
        statementWidth =
            deviceWidth -
            (noOfSides * OFFSET_MARGIN_FOR_MOBILE +
                noOfSides * MOBILE_VIEW_GAP +
                noOfSides * PARTIAL_WIDTH_FOR_MOBILE);
    } else if (
        isDesktopPreviewView != null &&
        !isDesktopPreviewView &&
        deviceWidth > MOBILE_WIDTH
    ) {
        statementWidth = MOBILE_DIMENSIONS;
    } else {
        statementWidth = DESKTOP_STATEMENT_WIDTH;
    }
    const itemWidth = statementWidth + bordersTotalWidth;
    const TOTAL_ITEM_WIDTH =
        itemWidth * sequencedVariables.length - 1 + gap * (sequencedVariables.length - 3);
    const transformValue = computeCarrouselStatementTransform(
        TOTAL_ITEM_WIDTH,
        itemWidth,
        gap,
        carrouselActiveVariableIndex,
        isRtl,
    );
    const handleSwipe = (direction: string) => {
        handleNavigationButtonClick(direction);
    };

    return (
        <div
            className={classes.container}
            ref={containerRef}
            role="region"
            aria-live="off"
            onFocusCapture={handleCarouselFocusIn}
            onBlurCapture={handleCarouselFocusOut}
        >
            <PoliteStatusPortal className="carrousel-sr-live-region">{liveAnnouncementText}</PoliteStatusPortal>
            <div className={classes.statementsContainer}>
                {sequencedVariables.map((variable, index) => (
                    <div
                        key={variable.name}
                        className={classes.statementSlideWrapper}
                        style={{
                            transform: `translateX(${transformValue}px)`,
                        }}
                    >
                        {index === 0 && isShowLottieGesture && (
                            <img
                                src={lottieGif}
                                className="lottie-gesture"
                                alt="Swipe gesture hint"
                            />
                        )}
                        <Statement
                            id={variable.elementId}
                            key={variable.name}
                            color={getSettingString(variable, "Color", "#F2F2F2") || "#F2F2F2"}
                            text={translate(variable, "text", language) || ""}
                            imageUrl={translate(variable, "imageUrl", language) || ""}
                            isMobileView={isMobileView}
                            isActiveItem={carrouselActiveVariableIndex === index}
                            statementWidth={statementWidth}
                            handleSwipe={handleSwipe}
                            isShowLottieGesture={index === 0 && isShowLottieGesture}
                            carouselStatementPositionLabel={resolveCarouselAriaLabel(
                                questionnaire.theme,
                                "CarouselAriaCurrentStatement",
                                language,
                                index + 1,
                                sequencedVariables.length,
                                "Statement {current} of {total}",
                            )}
                            ref={statementRefs.current[index]}
                        />
                    </div>
                ))}
            </div>
            {isAboveChoiceNavigation ? (
                <WrapperWithNavigationButtons
                    {...commonProps}
                    backButtonText={backButtonWithPosition}
                    nextButtonText={nextButtonWithPosition}
                    prevArrowRef={abovePrevArrowRef}
                    prevArrowOnKeyDown={abovePrevArrowOnKeyDown}
                    nextArrowRef={aboveNextArrowRef}
                    nextArrowOnKeyDown={aboveNextArrowOnKeyDown}
                />
            ) : (
                ""
            )}
            <div className={classes.choiceAndNavigationContainer}>
                <ControlContainer
                    controlType={ChoiceListControl}
                    onAfterSelection={() => {
                        onAfterCarouselChoiceSelection(true);
                    }}
                    isSingleOnPage={props.isSingleOnPage}
                    onNavigation={props.onNavigation}
                    {...getRendererProps(props)}
                />
                {isBelowChoiceNavigation ? (
                    <WrapperWithNavigationButtons
                        {...commonProps}
                        backButtonText={backButtonWithPosition}
                        nextButtonText={nextButtonWithPosition}
                        prevArrowRef={belowPrevArrowRef}
                        prevArrowOnKeyDown={belowPrevArrowOnKeyDown}
                        nextArrowRef={belowNextArrowRef}
                        nextArrowOnKeyDown={belowNextArrowOnKeyDown}
                    />
                ) : (
                    ""
                )}
            </div>
        </div>
    );
}
